import axios from 'axios';

import { User } from 'admin/users/Users';
import { Response } from 'core/util/Response';
import { SearchRequest } from 'core/util/Search';

export interface Page {
    locationID: string;
    locationUid: number;
    url: string;
    urlUid: number;
    pageID: string;
    pageName: string;
    logo: string;
    secondaryIDs: { [key: string]: string };
}

export interface Password {
    username: string;
    password: string;
    verificationDetails?: { [key: string]: string };
}

export interface AccessToken {
    token: string;
    secret: string;
    refreshToken: string;
    accountID?: string;
    scopes?: string[];
    email?: string;
    subscribed: boolean;
    states?: { [key: string]: boolean };
}

export interface Token {
    token: string;
}

export interface CredentialData {
    password?: Password;
    accessToken?: AccessToken;
    token?: Token;
}

export enum CredentialType {
    Password = 1,
    PageToken = 2,
    UserToken = 3,
    Token = 4,
    UserPassword = 5,
}

export enum AccountType {
    Pages = 1,
    Ads = 2,
    Project = 3,
}

export interface Account {
    type: AccountType;
    id: string;
    name: string;
    selected?: boolean;
    connected?: boolean;
}

export interface Scope {
    name: string;
    values: string[];
}
export interface Credential {
    id: string;
    credentialType: CredentialType;
    tenantID: number;
    sourceID: string;
    sourceUid: number;
    credentialData: CredentialData;
    valid: boolean;
    entityAttributes?: Scope[];
    page?: Page;
    parentID: string;
    message: string;
    createdDate: number;
    createdBy: string;
    createdByUser?: User;
    attached: boolean;
    accounts?: { [key in AccountType]: Account[] };
    refreshedDate: number;
    modules?: string[];
    userID: number;
    externalUserID: string;
    agencyID: number;
    defaultAccounts: { [key in AccountType]: string };
    defaultCredential: boolean;
    externalID?: string;
    authInfoID?: string;
    locationUids?: string[];
    locations?: [];
    invalidLocations?: [];
}

export enum FieldName {
    Attached = '__credential_attached__',
    LocationUid = '__credential_locationUID__',
    LocationUid1 = '__location_uid__',
    Modules = '__credential_modules__',
    SourceId = '__credential_sourceID__',
    Type = '__credential_type__',
    Valid = '__credential_valid__',
    Id = '__credential_id__',
    CreatedBy = '__credential_createdBy__',
    CreatedBy1 = '__credential_created_by__',
    CreatedDate = '__credential_createdDate__',
    CreatedDate1 = '__credential_created_date__',
    ExternalUserId = '__credential_externalUserID__',
    UpdatedBy = '__credential_updatedBy__',
    UpdatedDate = '__credential_updatedDate__',
    UpdatedDate1 = '__credential_updated_date__',
    LocationMatchingJobId = '__locationurlmatchingjob_id__',
    LocationSourceUrlsSource = '__location_sourceUrls__.source',
    LocationId = '__location_id__',
    TenantId = '__credential_tenantID__',
    AgencyId = '__credential_agency_id__',
    ParentId = '__credential_parentID__',
    Missing = '__credential_missing__',
}

export interface SaveCredentialResponse {
    credential: Credential;
    response: Response;
}

export interface LocationUidSourceId {
    locationUid: number;
    sourceId: any;
}

export const getCredentials = async (
    agencyId: number,
    tenantId: number,
    search: SearchRequest,
    args?: any,
): Promise<Credential[]> => {
    const params = { agencyId, tenantId, search, args };

    const { data } = await axios.post(`/api/tenants/${tenantId}/credentials/search-credentials`, params);

    return data;
};

export const invalidateCredential = async (tenantId: number, credentialId: any): Promise<any> => {
    const params = { tenantId, credentialId };

    const { data } = await axios.put(`/api/tenants/${tenantId}/credentials/invalidate/${credentialId}`, params);

    return data;
};

export const saveCredential = async (
    tenantId: number,
    credential: Partial<Credential>,
): Promise<SaveCredentialResponse> => {
    const { data } = await axios.post(`/api/tenants/${tenantId}/credentials`, credential);

    return data;
};

export const savePasswordCredential = async (tenantId: number, credential: Partial<Credential>): Promise<any> => {
    const { data } = await axios.post(`/api/tenants/${tenantId}/credentials/v1/savePasswordCredential`, credential);

    return data;
};

export const getCredentialStatusForLocations = async (
    agencyId: number,
    tenantId: number,
    credentialStatusRequest: any,
    args?: any,
): Promise<any> => {
    const params: any = { agencyId, tenantId, credentialStatusRequest, args };

    const { data } = await axios.get(`/api/tenants/${tenantId}/credentials/credential-status`, {
        params,
    });

    return data;
};
